import React, { useEffect, useState } from "react";
import {
  IoIosArrowRoundBack,
  IoIosArrowRoundForward,
  IoIosPersonAdd,
} from "react-icons/io";
import { RotatingLines } from "react-loader-spinner";
import { Link, useParams } from "react-router-dom";
import { useGlobalContext } from "../../context/stateContext";
import { useAuth } from "../../context/Auth";
import ExportData from "../../components/ExportData";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import ModifyReturnItemModal from "./ModifyReturnItemModal";


const ItemReturnDetails = () => {
  const { sale_id } = useParams();
  const { requestWithAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [salesList, setSalesList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [approving, setApproving] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modal, setModal] = useState(false);

  const returnSale = (sale_id, item_id) => {
    Swal.fire({
      icon: "question",
      title: `Are you sure you want to return this item?`,
      showDenyButton: true,
      confirmButtonText: "Yes Proceed!",
    }).then(async (result) => {
      /* Read more about isConfirmed, isDenied below */
      if (result.isConfirmed) {
        setApproving(true);
        const body=JSON.stringify({
            return_type:"partial",
            item_id:item_id
        })
        try {
          const data = await requestWithAuth(
            `/inventory/return-sale/${sale_id}/`,
            "POST", body,{
                'Content-Type':"application/json"
            }
          );
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: "Sale returned Successfully",
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {
          toast.error("Something unexpected happened.");
          console.error("Error updating drug:", error);
        } finally {
          setApproving(false);
        }
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  const fetchSaleItemList = async (page_size, search_query, current_page) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/sale-items/${sale_id}?page=${current_page}&pageSize=${page_size}&search=${search_query}`,
        "GET"
      );
      setSalesList(data?.results);
      setNextPage(data?.next);
      setPrevPage(data?.prev);
      setTotalPages(Math.ceil(data?.count / pageSize));
    } catch (error) {
      console.error("Error fetching list:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTableData = async (page_size, search_query, current_page) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/sale-item-return?page=${current_page}&pageSize=100000000000000000000000000000&search=${search_query}`,
        "GET"
      );
      setTableData(data?.results);
    } catch (error) {
      console.error("Error fetching list:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterSaleList = (search_query) => {
    setSearch(search_query);
    setCurrentPage(1); // Reset to page 1 whenever search query changes
  };

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3; // Number of pages to show around the current page
    const totalPagesToShow = 5; // Total pages to show at start and end

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    // Always show the first page
    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    // Show the numbers around the current page
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    // Always show the last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number) => (
      <button
        key={number}
        onClick={() => number !== "..." && handlePageClick(number)}
        className={
          number === currentPage
            ? "border border-[#979797] bg-gradient-to-b from-white to-[#dcdcdc] py-[0.5rem] px-[1rem]"
            : "py-[0.5rem] px-[1rem]"
        }
      >
        {number}
      </button>
    ));
  };

  useEffect(() => {
    fetchSaleItemList(pageSize, search, currentPage);
    getTableData(pageSize, search, currentPage);
  }, [search, currentPage, pageSize]);

  return (
    <div className="p-[20px]">
        {modal && selectedItem && (
        <ModifyReturnItemModal
          modalOpen={modal}
          setModalOpen={setModal}
          sale_item={selectedItem}
        />
      )}
      <div className="py-[32px] flex items-center justify-between ">
        <div className="flex items-center gap-3">
          <IoIosPersonAdd className="text-[19px]" />
          <h4 className="text-[19px]">
            <b>Return </b> - Details
          </h4>
        </div>
      </div>

      <div className="border border-[#ddd] bg-white">
        <div className="p-[20px]">
          <h5 className="my-[2px] text-[17px]">Return Items</h5>
        </div>
        <div className="p-[20px]">
          <div className="p-[20px] border border-[#ddd]">
            <div className="pt-[20px] flex lg:flex-row flex-col justify-between mb-[20px]">
              <div className=" flex items-center gap-5">
                <ExportData tableData={tableData} fileName="Expense Report" />
                <div className="flex items-center gap-2">
                  <b>Show:</b>
                  <select
                    name=""
                    id=""
                    onChange={(e) => setPageSize(e.target.value)}
                    className="border border-[#ddd] rounded-[3px] py-[7px] px-[12px] text-[13px]"
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <b>Filter:</b>
                <input
                  type="text"
                  placeholder="Type to filter..."
                  onChange={(e) => filterSaleList(e.target.value)}
                  className="outline-none border border-[#ddd] rounded-[3px] h-[36px] w-full text-[#333] text-[13px] px-[12px] py-[7px]"
                />
              </div>
            </div>
            <div className="overflow-auto ">
              <table className="border-collapse w-full">
                <thead>
                  <tr>
                    <th className="border-b border-t border-[#bbb] py-[12px] px-[20px]">
                      Invoice ID
                    </th>
                    <th className="border-b border-t border-[#bbb] py-[12px] px-[20px]">
                      Description
                    </th>
                    <th className="border-b border-t border-[#bbb] py-[12px] px-[20px]">
                      Item Cost
                    </th>
                    <th className="border-b border-t border-[#bbb] py-[12px] px-[20px]">
                      Quantity
                    </th>

                    <th className="border-b border-t border-[#bbb] py-[12px] px-[20px]">
                      Purchased Date
                    </th>

                    <th className="border-b border-t border-[#bbb] py-[12px] px-[20px]">
                      Processed By
                    </th>
                    <th className="border-b border-t border-[#bbb] py-[12px] px-[20px]">
                      Action
                    </th>
                  </tr>
                </thead>
                {!loading && (
                  <tbody>
                    {salesList?.map((item, index) => (
                      <tr key={index} className="text-center">
                        <td className="text-center border-b border-[#bbb] py-[12px] px-[20px]">
                          {item?.sale}
                        </td>
                        <td className="text-center border-b border-[#bbb] py-[12px] px-[20px]">
                          {item?.drug}
                        </td>
                        <td className=" text-center border-b border-[#bbb] py-[12px] px-[20px]">
                          {item?.total_selling_price}
                        </td>
                        <td className="text-center border-b border-[#bbb] py-[12px] px-[20px]">
                          {item?.quantity}
                        </td>
                        <td className="text-center border-b border-[#bbb] py-[12px] px-[20px]">
                          {item?.created_at}
                        </td>

                        <td className="text-center border-b border-[#bbb] py-[12px] px-[20px]">
                          {item?.recorded_by}
                        </td>

                        {!approving ? (
                          <td className="border-b border-[#bbb] py-[12px] px-[20px] flex items-center justify-center gap-1">
                            <div
                              onClick={() => {
                                returnSale(item?.sale_id, item?.id);
                              }}
                              className="hover:bg-[#a12800] bg-[#d40000] cursor-pointer text-[10px] pt-[3px] px-[5px] pb-[1px] text-white"
                            >
                              RETURN ITEM
                            </div>
                            <div
                              onClick={()=>{setModal(true); setSelectedItem(item)}}
                              className="hover:bg-[#008fa1] bg-[#00BCD4] cursor-pointer text-[10px] pt-[3px] px-[5px] pb-[1px] text-white"
                            >
                              MODIFY ITEM
                            </div>
                            
                          </td>
                        ) : (
                          <td className="border-b border-[#bbb] py-[12px] px-[20px] flex items-center justify-center gap-1">
                            <RotatingLines
                              visible={true}
                              height="16"
                              width="16"
                              color="black"
                              strokeColor="black"
                              strokeWidth="5"
                              animationDuration="0.75"
                              ariaLabel="rotating-lines-loading"
                              wrapperStyle={{}}
                              wrapperClass=""
                            />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
              {loading && (
                <div className="flex items-center justify-center py-10">
                  <RotatingLines
                    visible={true}
                    height="30"
                    width="30"
                    color="black"
                    strokeColor="black"
                    strokeWidth="5"
                    animationDuration="0.75"
                    ariaLabel="rotating-lines-loading"
                    wrapperStyle={{}}
                    wrapperClass=""
                  />
                </div>
              )}
              {salesList?.length <= 0 && !loading && (
                <p className="text-center py-5">No data availabe in table</p>
              )}
              <div className="flex justify-between pt-[20px]">
                <p className="text-[12px] text-[#888]">
                  Showing {currentPage} of {totalPages} pages
                </p>
                <div className="flex items-center gap-2">
                  <IoIosArrowRoundBack
                    className="text-[25px]"
                    onClick={handleNextPage}
                  />
                  {renderPageNumbers()}
                  <IoIosArrowRoundForward
                    className="text-[25px]"
                    onClick={handlePreviousPage}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemReturnDetails;
